import { useRef, useState, useCallback } from "react";
import { Row, Col, Container } from './Grid';
import SignatureCanvas from "react-signature-canvas";
import { FloatingLabel } from "./FloatingLabel";
import PropTypes from 'prop-types';
import { DefaultButton } from "./DefaultButton";
import { HiddenValidationControl } from "./HiddenValidationControl";
import UnavailableComponent from "./UnavailableComponent";
import { withComponentCapabilities } from "./capabilities/componentCapabilities";
import { materializeValue } from "../utils/lazyValue";
import { Modal } from "../utils/dialogUtlities";

export const customCtrlDef = {
    toString: async ({ value, defaultValue }) => {
        value = await materializeValue(value);
        if (!value) {
            return defaultValue; 
        }
        return '<signature>';
    },
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, href, ...props }) => <Signature href={href ?? def.href} {...props}></Signature>
}

/**
 * A component to capture and display an e-ink signature
 * @component
 */
const Signature = withComponentCapabilities(({ value, disabled = false, placeholder, hideLabel = false, required = false, onChange, href}) => {
    const canvas = useRef();
    const [width, setWidth] = useState(100);
    const [showCapture, setShowCapture] = useState(false);

    const elementRef = useCallback(node => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => { 
            const w = node.getBoundingClientRect()?.width;
            setWidth(w ?? 100);
        });
        resizeObserver.observe(node);
      }, []);
      

    const handleClose = newValue => {
        if (newValue !== false) {
            onChange(newValue);
        }
        setShowCapture(false);
    }

    const handleSave = () => {
        const trimmedCanvas = canvas.current?.getTrimmedCanvas();
        if (trimmedCanvas) {
            const imageData = trimmedCanvas.getContext('2d').getImageData(0, 0, trimmedCanvas.width, trimmedCanvas.height);
            const data = imageData.data;
            // Loop over each pixel to check if it's not the default state
            for (let i = 0; i < data.length; i += 4) {
                // Check if the pixel is not transparent (default is fully transparent)
                if (data[i] !== 0 || data[i + 1] !== 0 || data[i + 2] !== 0 || data[i + 3] !== 0) {
                    handleClose(trimmedCanvas.toDataURL('image/png'));
                    return;
                }
            }
            handleClose(required ? false : null);
        } else {
            handleClose(required ? false : null);
        }
    }

    if (value === true) {
        return <UnavailableComponent placeholder={placeholder} text='unavailableComponentMessage'></UnavailableComponent>
    }

    return <>
    <Modal show={showCapture && !disabled} onHide={() => handleClose(false)} size="lg">
        <Modal.Header>Signature</Modal.Header>
        <Modal.Body>
            <div className="w-100" ref={elementRef}>
            {showCapture ? <SignatureCanvas canvasProps={{width: width, height: '150px'}} ref={ref => canvas.current = ref}></SignatureCanvas> : null}
            </div>
        </Modal.Body>
        <Modal.Footer>
                <Container fluid>
                    <Row>
                        <Col>
                            <DefaultButton kind="clear" onClick={() => canvas.current?.clear()}></DefaultButton>
                        </Col>
                        <Col className="text-end">
                            <DefaultButton kind="close" onClick={() => handleClose(false)}></DefaultButton>
                            <DefaultButton kind="save" onClick={handleSave}></DefaultButton>
                        </Col>
                    </Row>
                </Container>
            </Modal.Footer>
    </Modal>
    <FloatingLabel placeholder={placeholder} hideLabel={hideLabel} required={required} value={value} href={href}>
        <Row style={{borderBottom: '1px solid black', minHeight: '100px', cursor: disabled ? null : 'pointer', alignItems: 'end'}} onClick={() => !disabled ? setShowCapture(true) : null}>
            <Col xs={'auto'} className="px-2" style={{fontWeight: 'bold', fontSize: 'larger'}}>
                X
            </Col>
            <Col>
                {value && <img alt="signature" style={{ objectFit: 'contain', maxWidth: '100%', maxHeight: '150px'}} src={value}></img>}
            </Col>
        </Row>
        <HiddenValidationControl value={value || null} required={required} style={{ height: '4px' }}></HiddenValidationControl>
    </FloatingLabel>
    </>;
}, {

});

Signature.propTypes = {
    /**
     * The image to be displayed by the control - base64 encoded data URL
     */
    value: PropTypes.string,

    /**
     * Function to call when the image has been updated
     */
    onChange: PropTypes.func.isRequired,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default Signature;